import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { SelectedFilterUpdatesService } from 'src/app/common/bhub-common-module/services/selected-filter-updates.service';
import { CalcEngineService } from 'src/app/services/calc-engine.service';
import { MetricService } from 'src/app/services/metric.service';
import { UserBoardComponentMode } from 'src/app/services/models/user-board.model';
import { CalcEngineConstants, CalcEngineRequest, CalcEngineResponse, Metric } from 'src/app/services/models/calc-engine.model';
import { StoredTableTileDefinition, TableMetric } from './table-tile.model';
import { GroupByOption, ITileComponent, StoredMetric } from '../common-tile.model';
import { Dataset, DatasetType } from 'src/app/services/models/datasets.models';
import { DatasetService } from 'src/app/services/dataset.service';

@Component({
  selector: 'bhub-table-tile',
  standalone: false,
  templateUrl: './table-tile.component.html',
  styleUrl: './table-tile.component.scss'
})
export class TableTileComponent implements OnInit, ITileComponent {
  @Input() data: StoredTableTileDefinition;
  @Input() mode: UserBoardComponentMode;

  @Output() onPreviewItemClickEvent = new EventEmitter<string>();

  //Make enum available for html
  UserBoardComponentMode = UserBoardComponentMode;
  //This needs to come from a service instead
  availableMetrics: StoredMetric[];
  selectedMetrics: StoredMetric[] = [];
  filtersApplied: any = {};

  public initialized = false;
  public model: StoredTableTileDefinition = new StoredTableTileDefinition();
  public rows;
  public columns;
  private _selectedGroupBys: GroupByOption[] = [];

  get selectedGroupBys(): GroupByOption[] {
    return this._selectedGroupBys;
  }
  set selectedGroupBys(value: GroupByOption[]) {
    if (value != null) {
      this._selectedGroupBys = value;
    }
  }

  public groupByDatasets: {dataset: Dataset, fields: {fieldId: number, fieldName: string}[]}[] = [];
  public availableFields: {fieldId: number, fieldName: string}[] = [];
  public selectedGroupBy: number | null = null;
  public selectedField: {fieldId: number, fieldName: string} | null = null;
  public selectedMetric: StoredMetric | null = null;


  metricsVerticalOrHorizontal: string = 'horizontal'; // Default to horizontal


  constructor(
    private calcEngineService: CalcEngineService,
    private metricService: MetricService,
    private selectedFilterUpdateObserver: SelectedFilterUpdatesService,
    private datasetService: DatasetService,
    private cdr: ChangeDetectorRef
  ) {
  }

  public validate(): string{
    if(this.model.groupByOptions.length == 0){
      return 'Please select at least one group by option';
    } else if(this.model.metrics.length == 0){
      return 'Please select at least one metric';
    }
    else if(this.model.defaultGroupBys.length == 0){
      return 'Please select the default group by option(s)';
    }
    else {
      return '';
    }
  }

  public ngOnInit() {
    //Set initial state of the filters as well as capture updates and make service calls when they change

    this.datasetService.getDatasetReferences().subscribe((datasets: Map<number, Dataset>) => {
      this.groupByDatasets = Array.from(
        datasets.values())
        .filter((dataset) => dataset.datasetReferenceTable.datasetType === DatasetType.Reference || dataset.datasetReferenceTable.datasetType === DatasetType.View)
        .map((dataset) => {
          return {
            dataset: dataset,
            fields: dataset.dataTableSchemas.map(s => ({fieldId: s.fieldId, fieldName: s.displayName}))
          }
        });

        this.selectedFilterUpdateObserver.filters$.subscribe((filters) => {
          this.filtersApplied = filters;
          this.makeServiceRequest();
        });
      });

  }

  makeServiceRequest(): void{
    this.initialized = false;

    if(this.mode === UserBoardComponentMode.Live){
      var request = this.generateTileRequest(this.data);
      this.calcEngineService.runCalcEngine(request, false).subscribe( (response) => {
        this.buildModelFromResponse(this.data, response);
        this.initialized = true;
      });
    }
    else if( this.mode === UserBoardComponentMode.Preview) {
      this.buildModelFromMockResponse(this.data);
      this.initialized = true;
    }
    else if( this.mode === UserBoardComponentMode.Edit) {
      if(this.data != null){
        this.buildInputModel(this.data);
        this.initialized = true;
      } else{
        this.buildInputModel();
        this.initialized = true;
      }
    }
  }

  showPrimaryKey(): boolean{
    if(this.selectedGroupBy == null) return false;
    return this.getDataset(this.selectedGroupBy).dataTableSchemas.filter(schema => schema.isPrimaryKey).map(schema=>schema.fieldName).length > 1;
  }

  getPrimaryKeyValue(){
    return CalcEngineConstants.PRIMARY_KEY;
  }

  generateTileRequest(data: StoredTableTileDefinition): CalcEngineRequest {
    const request = new CalcEngineRequest();

    // Use the primary key from the currently selected group bys (if any)
    if (this.selectedGroupBys && this.selectedGroupBys.length > 0) {
      this.selectedGroupBys.forEach(groupBy => {
        // Add the primary key to groupBy
        request.groupBy.push(groupBy.datasetPrimaryKey);

        // Add to additionalFields if fieldIdToDisplay is different from primary key
        if (groupBy.fieldIdToDisplay !== groupBy.datasetPrimaryKey) {
          if (!request.additionalFields[groupBy.datasetId]) {
            request.additionalFields[groupBy.datasetId] = [];
          }
          request.additionalFields[groupBy.datasetId].push(groupBy.fieldIdToDisplay);
        }
      });
    }
    // If no group bys selected, request.groupBy will remain empty (valid for aggregated data)

    request.metricList = data.metrics.map((metric) => metric.metric as Metric);
    request.filters = this.filtersApplied;

    return request;
  }

  onGroupByChange(datasetId: number, field: {fieldId: number, fieldName: string}) {
    const existingIndex = this.model.groupByOptions.findIndex(g => g.datasetId === datasetId.toString());

    if (existingIndex !== -1) {
      // Find the dataset to get its primary key field
      const dataset = this.getDataset(datasetId);
      if (!dataset) return;

      // Find the primary key field(s)
      const primaryKeyFields = dataset.dataTableSchemas.filter(schema => schema.isPrimaryKey);
      if (primaryKeyFields.length === 0) return;

      const primaryKeyFieldId = primaryKeyFields[0].fieldId.toString();
      const fieldIdToDisplay = field.fieldId.toString();

      this.model.groupByOptions[existingIndex].datasetPrimaryKey = primaryKeyFieldId;
      this.model.groupByOptions[existingIndex].fieldToDisplay = field.fieldName;
      this.model.groupByOptions[existingIndex].fieldIdToDisplay = fieldIdToDisplay;
    }

    // Optional: remove if field is cleared
    if (!field) {
      this.model.groupByOptions = this.model.groupByOptions.filter(g => g.datasetId !== datasetId.toString());
    }
  }

    // Build a new model from the service response and data
  buildModelFromResponse(data: StoredTableTileDefinition, response: CalcEngineResponse): void {

        // Note: We respect the user's current selection of group bys
    // If they've deselected all group bys, we show aggregated data (no grouping)

    // Handle case where no group bys are selected (valid use case for aggregated data)
    const hasGroupBys = this.selectedGroupBys && this.selectedGroupBys.length > 0;

        // Get the group by field names for column headers (only if group bys exist)
    const groupByFieldNames = hasGroupBys ? this.selectedGroupBys.map(groupBy =>
      this.getDatasetName(groupBy.datasetId) + ' - ' + groupBy.fieldToDisplay
    ) : [];

    // Get metric names for column headers
    const metricNames = data.metrics.map(metric => metric.metric.metricName);

    // Create column definitions
    this.columns = [
      ...groupByFieldNames.map(fieldName => ({
        field: fieldName,
        header: fieldName,
        sortable: true,
        filter: true
      })),
      ...metricNames.map(metricName => ({
        field: metricName,
        header: metricName,
        sortable: true,
        filter: true
      }))
    ];

    // Process the rowData array to create proper row objects
    this.rows = [];

    response.rowData.forEach((rowObject) => {
      const newRow: any = {};

      if (hasGroupBys) {
        // For each group by, map the dataset ID to the display field name
        this.selectedGroupBys.forEach((groupBy) => {
          const fieldName = this.getDatasetName(groupBy.datasetId) + ' - ' + groupBy.fieldToDisplay;
          // Use fieldIdToDisplay to get the correct value from the response
          // This will be either the primary key value or the additional field value
          newRow[fieldName] = rowObject[groupBy.fieldIdToDisplay] || '';
        });
      }

      // Add metric values (any non-dataset ID keys are metric names)
      Object.entries(rowObject).forEach(([key, value]) => {
        // Skip the fields that we already processed for group bys (both primary key and display fields)
        if (hasGroupBys && this.selectedGroupBys.some(gb => gb.datasetPrimaryKey === key || gb.fieldIdToDisplay === key)) {
          return; // Skip group by fields, already processed above
        }
        // This should be a metric value
        newRow[key] = value;
      });

      this.rows.push(newRow);
    });

    this.model = data;
  }


  // Build a new model from the service response and data
  buildModelFromMockResponse(data: StoredTableTileDefinition): void {

    this.model = data;
    this.selectedGroupBys = data.defaultGroupBys;
    this.populatePreviewWithMockData();

  }

  // Build a new model from scratch
  buildInputModel(data: StoredTableTileDefinition = null): void {

    this.metricService.getMetricList().subscribe((metrics) => {

        //set convert service response to available metrics list
        this.availableMetrics = [];
        metrics.forEach((metric) => {
          this.availableMetrics.push(new StoredMetric(metric.metricId, metric.metricName));
        });

        if(data == null){
          this.model = new StoredTableTileDefinition();
          this.rows = [];
          this.columns = [];
          this.selectedGroupBys = [];
        }
        else {
          this.model = data;
          this.selectedGroupBys = data.defaultGroupBys;
          this.populatePreviewWithMockData();
        }

        /// TODO Consider adding logic to present text to user telling them to create metrics if none are available
    });
  }

  populatePreviewWithMockData() {
    if(this.model.metrics.length == 0){
      this.rows = [];
      this.columns = [];
      return;
    }

    // Handle case where no group bys are selected (valid use case for aggregated data)
    const hasGroupBys = this.selectedGroupBys && this.selectedGroupBys.length > 0;

    // Get the group by field names for column headers (only if group bys exist)
    const groupByFieldNames = hasGroupBys ? this.selectedGroupBys.map(groupBy =>
      this.getDatasetName(groupBy.datasetId) + ' - ' + groupBy.fieldToDisplay
    ) : [];

    // Get metric names for column headers
    const metricNames = this.model.metrics.map(m => m.metric.metricName);

    // Create column definitions (consistent with buildModelFromResponse)
    this.columns = [
      ...groupByFieldNames.map(fieldName => ({
        field: fieldName,
        header: fieldName,
        sortable: true,
        filter: true
      })),
      ...metricNames.map(metricName => ({
        field: metricName,
        header: metricName,
        sortable: true,
        filter: true
      }))
    ];

    // Create mock rows
    this.rows = [];
    for(let i = 0; i < 5; i++){
      var newRow = {};
      this.columns.forEach((col, index) => {
          newRow[col.field] = 'XXXXX';
      });
      this.rows.push(newRow);
    }
  }

  //Edit Mode methods:
  onMetricChange(event: any): void {
    // Find the selected metric based on the selected metricId
    this.selectedMetrics.push(this.availableMetrics.find(metric => metric.metricId === event.metricId));

    // if (this.selectedHeroMetric) {
    //   // Set the model.heroRow.value to the selected metric object
    //   this.model.heroRow.value = this.selectedHeroMetric;
    //   // Also update displayMetricName to reflect the metricName
    //   this.displayMetricName = this.selectedHeroMetric.metricName;
    // }
  }

  sortColumn(event: any): void {
    const column = event.field;
    const sortOrder = event.order === 1 ? 'asc' : 'desc';

    this.rows.sort((a, b) => {
      if (a[column] < b[column]) {
        return sortOrder === 'asc' ? -1 : 1;
      }
      if (a[column] > b[column]) {
        return sortOrder === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  onDatasetChange() {
    // clear selectedField
    this.selectedField = null;
  }

  getDatasetFieldsToDisplay(): {fieldId: number, fieldName: string}[] {
    return this.groupByDatasets.find(dataset => dataset.dataset.datasetReferenceTable.datasetId === this.selectedGroupBy)?.fields || [];
  }

  getDatasetName(datasetId: string) {
    if(this.groupByDatasets.length == 0) return 'Unknown Dataset';
    const dataset = this.groupByDatasets.find(dataset => String(dataset.dataset.datasetReferenceTable.datasetId) === datasetId);
    return dataset?.dataset?.datasetReferenceTable?.datasetName || 'Unknown Dataset';
  }

  getDataset(datasetId: number) {
    return this.groupByDatasets.find(dataset => dataset.dataset.datasetReferenceTable.datasetId === datasetId)?.dataset;
  }

  onGroupByAdded(selectedGroupBy, selectedField) {
    //Double check this datasetId wasn't added already
    if(selectedGroupBy == null || selectedField == null) return;

    // Find the dataset to get its primary key field
    const dataset = this.getDataset(selectedGroupBy);
    if (!dataset) return;

    // Find the primary key field(s)
    const primaryKeyFields = dataset.dataTableSchemas.filter(schema => schema.isPrimaryKey);
    if (primaryKeyFields.length === 0) return;

    // Use the first primary key field ID
    const primaryKeyFieldId = primaryKeyFields[0].fieldId.toString();

    const fieldIdToDisplay = selectedField.fieldId.toString();

    if(this.model.groupByOptions.find(g => g.datasetId === String(selectedGroupBy)) == null){
      this.model.groupByOptions.push({
        datasetId: String(selectedGroupBy),
        datasetPrimaryKey: primaryKeyFieldId,
        fieldToDisplay: selectedField.fieldName,
        fieldIdToDisplay: fieldIdToDisplay
      });
      if(this.selectedGroupBys.length == 0){
        this.selectedGroupBys.push({
          datasetId: String(selectedGroupBy),
          datasetPrimaryKey: primaryKeyFieldId,
          fieldToDisplay: selectedField.fieldName,
          fieldIdToDisplay: fieldIdToDisplay
        });
      }
      this.populatePreviewWithMockData();
    }
  }

  onGroupByRemove(i){
    //Remove the group by from the selectedGroupBys array
    this.model.groupByOptions.splice(i, 1);
    this.populatePreviewWithMockData();
  }

  getGroupedByText(): string {
    if (!this.selectedGroupBys || this.selectedGroupBys.length === 0) {
      return 'Aggregated Data (No Grouping)';
    }
    return 'Grouped By: ' + this.selectedGroupBys.map(group => this.getDatasetName(group.datasetId)).join(', ');
  }

  onMetricAdd(metric: StoredMetric): void {
    // Find the selected metric based on the selected metricId
    if(this.model.metrics.find(m => m.metric.metricId === metric.metricId) == null){
      var tableMetric: TableMetric = new TableMetric();
      tableMetric.metric = metric;
      this.model.metrics.push(tableMetric);

      //Populate preview
      this.populatePreviewWithMockData();
    }
  }

  onMetricRemove(i: number): void {
    this.model.metrics.splice(i, 1);
    this.populatePreviewWithMockData();
  }

  onGroupBySelect(): void {
    // User clicked the "Select" button - trigger service request
    if (this.mode === UserBoardComponentMode.Live) {
      this.makeServiceRequest();
    } else if (this.mode === UserBoardComponentMode.Preview) {
      this.populatePreviewWithMockData();
    }
  }

  onGroupBySelectionChange(): void {
    // Trigger a new service request when group by selection changes
    if (this.mode === UserBoardComponentMode.Live) {
      this.makeServiceRequest();
    } else if (this.mode === UserBoardComponentMode.Preview) {
      this.populatePreviewWithMockData();
    }
  }

  onPreviewButtonClick(iconText: string) {
    this.onPreviewItemClickEvent.emit( iconText );
  }
}
